/**
 * Fulfillment / payment status pills.
 *
 * Colour carries meaning here, so it is chosen by state rather than looked up from a map with a
 * grey default: an unrecognised status from a future Shopify version must still be READABLE, not
 * silently rendered as neutral alongside real ones.
 */

const FULFILLMENT_TONE: Record<string, string> = {
  FULFILLED:
    "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200",
  UNFULFILLED:
    "bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-200",
  PARTIALLY_FULFILLED:
    "bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-200",
  IN_PROGRESS: "bg-sky-100 text-sky-900 dark:bg-sky-900/40 dark:text-sky-200",
  ON_HOLD: "bg-slate-200 text-slate-800 dark:bg-slate-700 dark:text-slate-100",
  SCHEDULED: "bg-sky-100 text-sky-900 dark:bg-sky-900/40 dark:text-sky-200",
};

const FINANCIAL_TONE: Record<string, string> = {
  PAID: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200",
  PARTIALLY_PAID:
    "bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-200",
  PENDING:
    "bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-200",
  REFUNDED: "bg-slate-200 text-slate-800 dark:bg-slate-700 dark:text-slate-100",
  PARTIALLY_REFUNDED:
    "bg-slate-200 text-slate-800 dark:bg-slate-700 dark:text-slate-100",
  VOIDED: "bg-rose-100 text-rose-900 dark:bg-rose-900/40 dark:text-rose-200",
  EXPIRED: "bg-rose-100 text-rose-900 dark:bg-rose-900/40 dark:text-rose-200",
};

/** UNFULFILLED → "Unfulfilled". Shopify's enums are the source; we only case them for reading. */
export function humanise(status: string): string {
  return status
    .toLowerCase()
    .split("_")
    .map((w, i) => (i === 0 ? w.charAt(0).toUpperCase() + w.slice(1) : w))
    .join(" ");
}

export function StatusBadge({
  status,
  kind,
}: {
  status?: string | null;
  kind: "fulfillment" | "financial";
}) {
  if (!status) return <span className="text-muted-foreground">—</span>;
  const tones = kind === "fulfillment" ? FULFILLMENT_TONE : FINANCIAL_TONE;
  const tone =
    tones[status] ??
    "bg-slate-200 text-slate-800 dark:bg-slate-700 dark:text-slate-100";
  return (
    <span
      className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${tone}`}
    >
      {humanise(status)}
    </span>
  );
}
